"use client"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { MessageCircle, Settings, AlertTriangle, WifiOff, Inbox, ExternalLink, Play, Mail, BotOff, BarChart3 } from "lucide-react"
import type { MessageAgentWithChannels } from "@/hooks/agents/messaging/use-agents"
import { ChannelIcon } from '@/components/ui/channel-icon'
import { AgentStatusBadge } from "./agent-status-badge"
import { AiReplyOffBadge, AiReplyOffHint } from "./ai-reply-off-badge"
import { AgentRoleBadge } from "@/components/agents/shared/agent-role-badge"
import { AGENT_PRIMARY_ROLE_USE_CASE } from "@/lib/ai/roles/types"
import { getChannelExternalUrl, sortChannelsForDisplay } from "./constants"
import { CHANNEL_LABELS, isAiReplyEnabled } from "@/lib/agents/messaging/types"
import { resolveOfflineMessage, summarizeOfflineChannels } from "@/lib/agents/messaging/channels/shared/offline-message"
import { AgentIntegrationBadges } from "@/components/agents/shared/agent-integration-badges"
import type { AgentIntegrationStatus } from "@/lib/agents/shared/integration-status-types"

const CHANNEL_STATUS_TOOLTIP: Record<string, string> = {
  connected: "Connected — messages are flowing",
  error: "Connection error — reconnect this channel",
  token_expired: "Authorization expired — reconnect this channel",
  disconnected: "Disconnected — this channel receives nothing",
}

interface AgentCardProps {
  agent: MessageAgentWithChannels
  onEdit: (agent: MessageAgentWithChannels) => void
  onViewConversations?: (agent: MessageAgentWithChannels) => void
  /** Omitted when the operator may not read analytics — gating lives with the caller. */
  onViewAnalytics?: (agent: MessageAgentWithChannels) => void
  onTest?: (agent: MessageAgentWithChannels) => void
  canManage: boolean
  integrationStatus?: AgentIntegrationStatus
}

export function MessagingAgentCard({
  agent,
  onEdit,
  onViewConversations,
  onViewAnalytics,
  onTest,
  canManage,
  integrationStatus,
}: AgentCardProps) {
  const channels = sortChannelsForDisplay(agent.channels || [])
  const connectedChannels = channels.filter((c) => c.status === "connected")
  const offline = summarizeOfflineChannels(channels, { agentDisabled: agent.status === "disabled" })
  const offlineSummary =
    agent.status === "draft"
      ? "Draft — customers reach nobody yet"
      : offline.offline > 0
        ? `Disabled — offline message on ${offline.offline} of ${offline.connected} channels`
        : "Disabled — customers get no reply"
  const errorChannels = channels.filter((c) => c.status === "error" || c.status === "token_expired")

  // A small badge alone got lost among the channel rows; the border reads at a glance.
  const statusAccent =
    agent.status === "disabled"
      ? "border-l-4 border-l-yellow-400"
      : agent.status === "draft"
        ? "border-l-4 border-l-gray-300"
        : ""

  return (
    <TooltipProvider>
    <Card
      className={`flex h-full flex-col cursor-pointer transition-shadow hover:shadow-md ${statusAccent}`}
      onClick={() => onEdit(agent)}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg font-medium text-gray-900 mb-1">
              {agent.name}
            </CardTitle>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <MessageCircle className="w-4 h-4" />
              <span>
                {connectedChannels.length === 0
                  ? "No channels"
                  : connectedChannels.length === 1
                    ? "1 channel"
                    : `${connectedChannels.length} channels`}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-2">
            {errorChannels.length > 0 && (
              <Badge
                variant="destructive"
                className="text-xs px-1.5 py-0.5"
                title={`${errorChannels.length} channel(s) need reconnection`}
              >
                <AlertTriangle className="w-3 h-3 mr-1" />
                <span>Error</span>
              </Badge>
            )}
            <AgentStatusBadge status={agent.status} />
            </div>
            {agent.status === "active" && !isAiReplyEnabled(agent.config) && <AiReplyOffBadge />}
            <AgentRoleBadge
              useCase={AGENT_PRIMARY_ROLE_USE_CASE.message_agent}
              roleSlugs={agent.config?.roles}
              roleLabel={agent.config?.role_label}
            />
            <AgentIntegrationBadges
              crmConnected={integrationStatus?.crmConnected ?? false}
              deliveryConnected={integrationStatus?.deliveryConnected ?? false}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col pt-0">
        <div className="flex h-full flex-1 flex-col space-y-3">
          {/* Channels list */}
          <div className="space-y-1">
            {channels.length === 0 ? (
              <div className="flex items-center gap-2 text-sm">
                <WifiOff className="w-4 h-4 text-gray-400" />
                <span className="text-gray-400 italic">No channel connected</span>
              </div>
            ) : (
              channels.map((ch) => {
                const url = getChannelExternalUrl(ch)
                const name = ch.account_name || CHANNEL_LABELS[ch.channel_type] || ch.channel_type
                return (
                  <div key={ch.id} className="flex items-center gap-2 text-sm">
                    <ChannelIcon channelType={ch.channel_type} size={16} className="shrink-0" />
                    {url ? (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        title={`Open ${name}`}
                        className="flex-1 min-w-0 inline-flex items-center gap-1 text-gray-600 font-medium hover:text-blue-600 hover:underline"
                      >
                        <span className="truncate">{name}</span>
                        <ExternalLink className="w-3 h-3 shrink-0" />
                      </a>
                    ) : (
                      <span className="text-gray-600 font-medium truncate flex-1 min-w-0">
                        {name}
                      </span>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="shrink-0 leading-none">
                          {ch.status === "connected" ? (
                            <span className="block h-2 w-2 rounded-full bg-green-500" />
                          ) : ch.status === "error" || ch.status === "token_expired" ? (
                            <span className="block h-2 w-2 rounded-full bg-red-500" />
                          ) : (
                            <WifiOff className="w-4 h-4 text-gray-400" />
                          )}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{CHANNEL_STATUS_TOOLTIP[ch.status]}</TooltipContent>
                    </Tooltip>
                    {ch.ai_replies_enabled === false && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-[10px] uppercase tracking-wide text-amber-700 shrink-0">
                            AI off
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          AI replies are switched off for this channel — your team answers here.
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {resolveOfflineMessage({ channel_type: ch.channel_type, config: ch.config ?? {} })
                      .enabled && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Mail className="h-3.5 w-3.5 text-gray-400 shrink-0" aria-label="Offline message enabled" />
                        </TooltipTrigger>
                        <TooltipContent>
                          Offline message is on — customers get it while nobody is answering here.
                        </TooltipContent>
                      </Tooltip>
                    )}
                    <Badge variant="outline" className="text-xs">
                      {CHANNEL_LABELS[ch.channel_type] || ch.channel_type}
                    </Badge>
                  </div>
                )
              })
            )}
          </div>

          {/* What a customer gets right now */}
          <ReplyStateLine agent={agent} offlineSummary={offlineSummary} />

          {/* Dates */}
          <div className="text-xs text-gray-500 pt-2 border-t">
            <span>Created:</span> {new Date(agent.created_at).toLocaleDateString()}
          </div>

          {/* Actions */}
          <div className="mt-auto flex flex-wrap gap-2 pt-5">
            {onTest && canManage && (
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={(event) => {
                  event.stopPropagation()
                  onTest(agent)
                }}
              >
                <Play className="w-4 h-4 mr-1" />
                <span>Test</span>
              </Button>
            )}
            {onViewConversations && (
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={(e) => {
                  e.stopPropagation()
                  onViewConversations(agent)
                }}
              >
                <Inbox className="w-4 h-4 mr-1" />
                <span>Conversations</span>
              </Button>
            )}
            {onViewAnalytics && (
              <Button
                variant="outline"
                size="sm"
                aria-label="Analytics"
                onClick={(e) => {
                  e.stopPropagation()
                  onViewAnalytics(agent)
                }}
              >
                <BarChart3 className="w-4 h-4" />
              </Button>
            )}
            {canManage && (
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={(e) => {
                  e.stopPropagation()
                  onEdit(agent)
                }}
              >
                <Settings className="w-4 h-4 mr-1" />
                <span>Settings</span>
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
    </TooltipProvider>
  )
}

/** What customers get right now: agent status first, then the agent-wide AI Reply switch. */
function ReplyStateLine({
  agent,
  offlineSummary,
}: {
  agent: MessageAgentWithChannels
  offlineSummary: string
}) {
  if (agent.status !== "active") {
    return (
      <div className="flex items-center gap-2 text-sm">
        <MessageCircle className="w-4 h-4 text-gray-400" />
        <span className="text-gray-500">{offlineSummary}</span>
      </div>
    )
  }
  if (!isAiReplyEnabled(agent.config)) {
    return (
      <AiReplyOffHint>
        <div className="flex items-center gap-2 text-sm" tabIndex={0}>
          <BotOff className="w-4 h-4 text-amber-500" />
          <span className="text-amber-700">AI Reply off — no AI replies on any channel</span>
        </div>
      </AiReplyOffHint>
    )
  }
  return (
    <div className="flex items-center gap-2 text-sm">
      <MessageCircle className="w-4 h-4 text-green-500" />
      <span className="text-green-600">
        AI reply {agent.config?.reply?.dry_run ? "(dry run)" : "enabled"}
      </span>
    </div>
  )
}
